/**
 * ExpandableLabel Component
 * Reusable expandable label with built-in accessibility and keyboard support
 */

import React from 'react';
import { ExpandIcon } from './ExpandIcon';

export interface ExpandableLabelProps {
  /** Label content to display (string or ReactNode for styled content) */
  label: React.ReactNode;
  /** Whether the section is currently expanded */
  isExpanded: boolean;
  /** Function to call when label is clicked/activated */
  onToggle: () => void;
  /** Additional CSS class name */
  className?: string;
  /** Optional ID for ARIA controls */
  ariaControls?: string;
  /** Custom styles for the label */
  style?: React.CSSProperties;
}

/**
 * ExpandableLabel component - Clickable label with expand/collapse functionality
 */
export const ExpandableLabel: React.FC<ExpandableLabelProps> = ({
  label,
  isExpanded,
  onToggle,
  className = '',
  ariaControls,
  style
}) => {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggle();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      onToggle();
    }
  };

  return (
    <div
      className={`planet-detail-label expandable-label ${className}`}
      style={style}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      aria-expanded={isExpanded}
      aria-controls={ariaControls}
      onKeyDown={handleKeyDown}
    >
      <ExpandIcon isExpanded={isExpanded} />
      <span>{label}</span>
    </div>
  );
};

export default ExpandableLabel;